import React, { useContext, useEffect, useState } from 'react';
import Moment from 'react-moment';

import { useParams } from '@gatsbyjs/reach-router';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import Confirm from '../../components/confirm';
import ConfirmWithReason from '../../components/confirm-with-reason';
import CustomDataGrid from '../../components/data-grid';
import JobRunTicker from '../../components/job-run ticker';
import LogViewer from '../../components/logViewer';
import SaveBackdrop from '../../components/saveBackdrop';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import API from '../../utils/api';
import {
  apiReponseError, apiRequestError, checkUserAuthenticated, formatDateTimeForDataGrid,
  getAppRoutePagePaths, getESPJobLogDetail, getESPJobLogs, getESPJobStatus, goToRoutePage,
  setWindowTitle, triggerESPJobs
} from '../../utils/helper';
import {
  EnlistmentIcon, JobIcon, JobStatusImage, MoreIcon, RefreshIcon, RunIcon, StopIcon, TriggerIcon,
  UploadIcon
} from '../../utils/icons';
import {
  Box, Breadcrumbs, Button, Grid, IconButton, Link, ListItemIcon, ListItemText, Menu, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography
} from '../../utils/material-ui-core';
import BatchLogFiles from './batchLogFiles';

const StyledMenuItem = withStyles((theme) => ({
  root: {
    "&:focus": {
      backgroundColor: theme.palette.secondary.main,
      "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const defaultEspJob = {
  espJobKey: "",
  name: "",
  runStatusDescription: "",
  lastModifiedBy: "",
  lastModifiedDate: null,
  lastTriggeredBy: "",
  lastTriggeredDate: null,
  runStatus: false,
  note: "",
  pendingCount: 0,
  lastProcessDate: null,
  listRoutePath: "",
  uploadRoutePath: "",
  canRead: false,
  canWrite: false,
  reviewCount: 0,
  triggerFileExists: false
};

const Jobs = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { writeLog, isUserAuthenticated, appRoutePaths, jobPagePathName, setJobPagePathName, espJobs, refreshData, showAlert } = useContext(AppContext);

  const { routeParamJobTitle } = useParams();

  const log1Columns = [
    { field: "jobId", headerName: "JobId", width: 150 },
    { field: "crtime", headerName: "Start", width: 220, type: "dateTime", valueFormatter: (params: GridValueFormatterParams) => formatDateTimeForDataGrid(params.value as string) },
    { field: "mtime", headerName: "End", width: 220, type: "dateTime", valueFormatter: (params: GridValueFormatterParams) => formatDateTimeForDataGrid(params.value as string) },
    { field: "status", headerName: "Status", width: 250, type: "string" },
    { field: "size", headerName: "Size", width: 150, type: "string" },
    { field: "fileKey", headerName: "Key", width: 150, hide: true },
  ];

  const [isPageInitialize, setIsPageInitialize] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [jobs, setJobs] = useState<AppPagePath[]>([] as AppPagePath[]);
  const [logData, setLogData] = useState<ESPJobLog[]>([]);
  const [espJobKey, setEspJobKey] = useState("");
  const [selectedEspJob, setSelectedEspJob] = useState<ESPJob>(defaultEspJob);
  const [selectedESPJobLog, setSelectedESPJobLog] = useState<ESPJobLog>({ jobId: 0, mtime: "", crtime: "", fileKey: "", size: "", status: "", showRunButton: false, espjobSubLogs: [{ jobId: 0, fileKey: "", status: "" }] });
  const [showLogDetail, setShowLogDetail] = useState(false);
  const [logDetailLoading, setLogDetailLoading] = useState(false);
  const [logDetail, setLogDetail] = useState("");
  const [openBatchLogFilesDialog, setOpenBatchLogFilesDialog] = useState<boolean>(false);
  const [triggerJobDialogOpen, setTriggerJobDialogOpen] = useState<boolean>(false);
  const [jobStatusDialogOpen, setJobStatusDialogOpen] = useState<boolean>(false);
  const [gridMoreAnchorEl, setGridMoreAnchorEl] = React.useState(null);

  // More menu click event
  const handleGridMoreMenuClick = (event) => {
    setGridMoreAnchorEl(event.currentTarget);
  };

  // Close more menu
  const handleGridMoreMenuClose = () => {
    setGridMoreAnchorEl(null);
  };

  // Handle datagrid click
  const handleDataGridClick = async (params: GridRowParams, event: any) => {
    const filterLogData = logData.filter((log) => log.fileKey === params.row.fileKey);

    if (filterLogData.length === 1) {
      if (filterLogData[0].espjobSubLogs.length > 1) {
        setSelectedESPJobLog(filterLogData[0]);
        setOpenBatchLogFilesDialog(true);
        return;
      } else {
        openLogDetail(params.row.fileKey);
      }
    } else {
      openLogDetail(params.row.fileKey);
    }
  };

  // Open log detail in a viewer
  const openLogDetail = async (fileKey: string) => {
    writeLog("Jobs Tab " + jobPagePathName, "Fetching requested log file content via api.", fileKey, "file");
    setLogDetail("");
    setLogDetailLoading(true);
    setShowLogDetail(true);

    const apiResponse = await getESPJobLogDetail(espJobKey, fileKey);

    if (apiResponse.success) {
      setLogDetail(apiResponse.data.content);
      setLogDetailLoading(false);
      writeLog("Jobs Tab " + jobPagePathName, "Opening file viewer.", fileKey, "file");
    } else {
      writeLog("Jobs Tab " + jobPagePathName, "Opening log file failed. " + apiResponse.errorMessage, fileKey, "file");
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      setLogDetailLoading(false);
      setShowLogDetail(false);
    }
  };

  // Show log files
  const handleBatchLogFilesClose = () => {
    setOpenBatchLogFilesDialog(false);
  };

  // Close log viewer
  const handleLogDetailCloseClick = () => {
    setShowLogDetail(false);
    writeLog("Jobs Tab " + jobPagePathName, "Closing file viewer.", "", "");
  };

  // Refresh page
  const refresh = async () => {
    writeLog("Jobs Page", "Refreshing jobs & logs", "", "");
    setLoading(true);

    await refreshData("espjob");

    if (jobPagePathName !== "Overview") {
      jobPagePathChanged(jobPagePathName);
    }
    setLoading(false);
  };

  // get batch log data
  const getBatchLogData = async (jobTitle) => {
    try {
      writeLog("Jobs Page", "Refreshing " + jobTitle + " logs", "", "");
      setSaveInProgress(true);
      setLogData([]);
      const apiResponse = await getESPJobLogs(jobTitle);

      if (apiResponse.success) {
        setLogData(apiResponse.data.espjoblogs);
        setSaveInProgress(false);
      } else {
        setSaveInProgress(false);
        writeLog("Jobs Page", "Error : Refreshing logs failed. " + apiResponse.errorMessage, "", "");
        showAlert({ message: apiResponse.errorMessage, severity: "error" });
      }
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  // Get confirmation before change job status
  const jobStatusDialogConfirmation = async (confirmWithReasonOutput: ConfirmWithReasonOutput) => {
    setJobStatusDialogOpen(false);
    if (confirmWithReasonOutput.confirm) {
      writeLog("Jobs Tab " + jobPagePathName, " Job status update confirmation model closed (yes).", espJobKey, "job");
      setSaveInProgress(true);
      await saveJobStatus(!selectedEspJob.runStatus, confirmWithReasonOutput.reason);
      await refreshEspJob();
      setSaveInProgress(false);
    } else {
      writeLog("Jobs Tab " + jobPagePathName, " Job status update confirmation model closed (no).", espJobKey, "job");
    }
  };

  // Save job status
  const saveJobStatus = async (runStatus: boolean, reason: string) => {
    const payload = {
      espjob: {
        runStatus: runStatus,
        note: reason,
      },
    };

    try {
      await API.post("/api/espjobs/" + espJobKey, payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          await refreshData("espjob");
        })
        .catch((error) => {
          writeLog("Jobs Tab " + jobPagePathName, "Error : Job status update failed.", espJobKey, "job");
          showAlert({
            message: apiReponseError(error, "Error job status update failed."),
            severity: "error",
          });
        });
    } catch (e) {
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  // Get confirmation before trigger job to rund
  const triggerJobDialogConfirmation = async (confirm: boolean) => {
    setTriggerJobDialogOpen(false);

    if (confirm) {
      writeLog("Jobs Tab " + jobPagePathName, " Trigger file confirmation model closed (yes).", espJobKey, "job");
      setSaveInProgress(true);
      await triggerJob(0);
      await refreshEspJob();
      setSaveInProgress(false);
    } else {
      writeLog("Jobs Tab " + jobPagePathName, " Trigger file confirmation model closed (no).", espJobKey, "job");
    }
  };

  // Trigger job to run
  const triggerJob = async (referenceId: number) => {
    try {
      writeLog("Jobs Tab " + jobPagePathName, "Update trigger file via api.", espJobKey, "job");

      const apiResponse = await triggerESPJobs(espJobKey, referenceId);
      if (apiResponse.success) {
        writeLog("Jobs Tab " + jobPagePathName, "Job trigger file updated successfully.", espJobKey, "job");
        showAlert({
          message: apiResponse.data.status.toString(),
          severity: "success",
        });
        await refreshData("espjob");
      } else {
        writeLog("Jobs Tab " + jobPagePathName, "Error : Job trigger file update failed. " + apiResponse.errorMessage, espJobKey, "job");
        showAlert({ message: apiResponse.errorMessage, severity: "error" });
      }
    } catch (e) {
      writeLog("Jobs Tab " + jobPagePathName, "Error : Job trigger file update failed. Something went wrong in our system.", espJobKey, "job");
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const selectEspJobKey = (jobTitle) => {
    let espKey = "";

    if (jobTitle !== "Overview") {
      setEspJobKey(espKey);
      setSelectedEspJob(defaultEspJob);

      const jobPaths = getAppRoutePagePaths("Jobs & Log", appRoutePaths);

      var filterJobs = jobPaths.filter((x) => x.title === jobTitle);
      if (filterJobs.length === 1) {
        espKey = filterJobs[0].key;
      }
      setEspJobKey(espKey);

      if (espKey !== "") {
        var filterEspJob = espJobs.filter((x) => x.espJobKey.toLowerCase() === espKey.toLowerCase());
        if (filterEspJob.length === 1) {
          setSelectedEspJob(filterEspJob[0]);
        }
      } else {
        jobPagePathChanged("Overview");
      }
    }

    return espKey;
  };

  const refreshEspJob = async () => {
    try {
      const apiResponse = await getESPJobStatus(espJobKey);
      if (apiResponse.success) {
        setSelectedEspJob(apiResponse.data.espJob);
      } else {
        writeLog("Jobs Tab " + jobPagePathName, "Error : Job status update failed. " + apiResponse.errorMessage, espJobKey, "job");
        showAlert({ message: apiResponse.errorMessage, severity: "error" });
      }
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  // Refresh batch log
  const refreshBatchLog = async () => {
    var tempespKey = selectEspJobKey(jobPagePathName);
    await getBatchLogData(tempespKey);
  };

  // Handle job change
  const jobPagePathChanged = async (jobTitle: string) => {
    writeLog("Jobs Page", "Tab changed to " + jobTitle.toLowerCase() + ".", "", "");
    setJobPagePathName(jobTitle);

    if (jobTitle !== "Overview") {
      var tempespKey = selectEspJobKey(jobTitle);

      await getBatchLogData(tempespKey);
    }
  };

  // Tab title
  const displayTabTitle = (title: string) => {
    return title.replace(/([A-Z]+)/g, " $1").replace(/^,/, "");
  };

  useEffect(() => {
    writeLog("Jobs Page", "", "", "");
    setWindowTitle("Jobs & Logs");
    window.scrollTo(0, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPagePathName]);

  useEffect(() => {
    const initializePage = async () => {
      if (isPageInitialize) {
        return;
      }

      checkUserAuthenticated(isUserAuthenticated);

      if (isUserAuthenticated) {
        const jobPaths = getAppRoutePagePaths("Jobs & Log", appRoutePaths);

        if (jobPaths.length <= 0) {
          goToRoutePage("home");
          return;
        }

        setJobs(jobPaths);

        await refreshData("espjob");

        let jobPagePathName2 = jobPagePathName;

        if (routeParamJobTitle !== undefined && routeParamJobTitle !== "" && routeParamJobTitle !== null) {
          var jobTitle = jobPaths.find((x) => x.title.toLocaleLowerCase().indexOf(routeParamJobTitle.toLocaleLowerCase()) >= 0)?.title;
          if (jobTitle !== undefined && jobTitle !== null && jobTitle !== "") {
            jobPagePathName2 = jobTitle;
          }
        } else {
          if (jobPagePathName === undefined || jobPagePathName === "" || jobPagePathName === null) {
            jobPagePathName2 = "Overview";
          }
        }
        jobPagePathChanged(jobPagePathName2);

        setTimeout(() => {
          setLoading(false);
        }, 1000);

        writeLog("Jobs Page", "Tab " + jobPagePathName.toLowerCase() + ".", "", "");

        setIsPageInitialize(true);
      }
    };
    setLoading(true);

    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appRoutePaths, isUserAuthenticated]);

  return (
    <React.Fragment>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <SaveBackdrop open={saveInProgress} />

          <ConfirmWithReason title="Confirmation" content={selectedEspJob.runStatus ? "Currently, the batch job is actively running.<p>Deactivate this job?</p>" : "Currently, the batch job is on hold.<p>Activate this job?</p>"} yesText={selectedEspJob.runStatus ? "PUT ON HOLD" : "ACTIVATE"} noText="CANCEL" open={jobStatusDialogOpen} showReason={selectedEspJob.runStatus} required={true} reasonLabel={"Reason"} callbackOnConfirm={jobStatusDialogConfirmation} />

          <Confirm title="Confirmation" content="Wish to trigger the batch job to run?" yesText="YES" noText="NO" open={triggerJobDialogOpen} callbackOnClose={() => setTriggerJobDialogOpen} callbackOnConfirm={triggerJobDialogConfirmation} />

          <BatchLogFiles open={openBatchLogFilesDialog} espJobKey={espJobKey} espJobLog={selectedESPJobLog} callRunButton={triggerJob} callbackOnClose={handleBatchLogFilesClose} />

          <LogViewer content={logDetail} okText="close" open={showLogDetail} loading={logDetailLoading} callbackOnClose={handleLogDetailCloseClick} />

          <Box className={pageStyles.root}>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <Box className={globalStyles.bottomSpacer}>
                  <Breadcrumbs style={{ paddingTop: "10px" }}>
                    <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                      Home
                    </Link>
                    <Typography color="textPrimary">Jobs</Typography>
                  </Breadcrumbs>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box display="flex" justifyContent="flex-end" width={1}>
                  <Tooltip arrow title="Menu">
                    <Button color="secondary" aria-controls="fade-menu" aria-haspopup="true" onClick={handleGridMoreMenuClick}>
                      <MoreIcon />
                    </Button>
                  </Tooltip>

                  <Menu elevation={2} id="grid-menu" anchorEl={gridMoreAnchorEl} keepMounted open={Boolean(gridMoreAnchorEl)} onClose={handleGridMoreMenuClose}>
                    <StyledMenuItem
                      onClick={(e) => {
                        writeLog("Jobs Tab Overview" + jobPagePathName, "Refresh esp job data.", "", "");
                        jobPagePathChanged("Overview");
                        handleGridMoreMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <JobIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={"Overview"} />
                    </StyledMenuItem>

                    {jobs.map((job, index) => (
                      <StyledMenuItem
                        key={index}
                        onClick={(e) => {
                          writeLog("Jobs Tab " + job.title, "Refresh esp job data.", "", "");
                          jobPagePathChanged(job.title as string);
                          handleGridMoreMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <JobIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={displayTabTitle(job.title)} />
                      </StyledMenuItem>
                    ))}

                    <hr />

                    {selectedEspJob.canWrite && selectedEspJob.uploadRoutePath !== "" ? (
                      <StyledMenuItem
                        onClick={(e) => {
                          writeLog("Jobs Tab " + jobPagePathName, "Upload new file.", "", "");
                          goToRoutePage(selectedEspJob.uploadRoutePath);
                          handleGridMoreMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <UploadIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={"Upload new file"} />
                      </StyledMenuItem>
                    ) : null}

                    {selectedEspJob.canRead && selectedEspJob.listRoutePath !== "" ? (
                      <StyledMenuItem
                        onClick={(e) => {
                          writeLog("Jobs Tab " + jobPagePathName, "Jump to pending review.", "", "");
                          goToRoutePage(selectedEspJob.listRoutePath);
                          handleGridMoreMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <EnlistmentIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={`Go To Pending Review ${selectedEspJob.reviewCount}`} />
                      </StyledMenuItem>
                    ) : null}

                    <StyledMenuItem
                      onClick={(e) => {
                        writeLog("Jobs Tab " + jobPagePathName, "Refresh esp job data.", "", "");
                        refresh();
                        handleGridMoreMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <RefreshIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={"Refresh"} />
                    </StyledMenuItem>
                  </Menu>
                </Box>
              </Grid>

              {jobPagePathName === "Overview" ? (
                <Grid item xs={12}>
                  <Box className={pageStyles.jobStatusContainer}>
                    <Grid container spacing={0}>
                      <Grid item xs={12}>
                        <Box p={0} className={pageStyles.jobImageContainer}>
                          <Box p={3} className={pageStyles.jobTitle}>
                            &nbsp;Job Status Summary
                          </Box>
                          <Box p={1}></Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Status</TableCell>
                                <TableCell>Job</TableCell>
                                <TableCell>Offline Note</TableCell>
                                <TableCell>Last Updated By</TableCell>
                                <TableCell align="right">Last Updated Date</TableCell>
                                <TableCell align="right">Pending</TableCell>
                                <TableCell align="right">Review </TableCell>
                                <TableCell align="right">Last Run Date</TableCell>
                                <TableCell>Last Triggered By</TableCell>
                                <TableCell align="right">Last Triggered Date</TableCell>
                                <TableCell>Next Job Run</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {jobs.map((job, index) =>
                                espJobs
                                  .filter((espJob) => espJob.espJobKey.toLocaleLowerCase() === job.key.toLocaleLowerCase())
                                  .map((espJob, index) => (
                                    <TableRow
                                      key={index}
                                      onClick={() => {
                                        jobPagePathChanged(job.title);
                                      }}
                                    >
                                      <TableCell>{espJob.runStatus ? <RunIcon fontSize="medium" className={pageStyles.jobSuccessStatus} /> : <StopIcon fontSize="medium" className={pageStyles.jobFailStatus} />}</TableCell>
                                      <TableCell>{job.title}</TableCell>
                                      <TableCell>{espJob.note}</TableCell>
                                      <TableCell>{espJob.lastModifiedBy}</TableCell>
                                      <TableCell align="right">{espJob.lastModifiedDate !== null && espJob.lastModifiedDate !== "" ? <Moment date={espJob.lastModifiedDate} format="MM/DD/YYYY hh:mm:ss" /> : ""}</TableCell>
                                      <TableCell align="right">{espJob.pendingCount > 0 ? espJob.pendingCount : ""}</TableCell>
                                      <TableCell align="right">{espJob.reviewCount > 0 ? espJob.reviewCount : ""}</TableCell>
                                      <TableCell align="right">{espJob.lastProcessDate !== null && espJob.lastProcessDate !== "" ? <Moment date={espJob.lastProcessDate} format="MM/DD/YYYY hh:mm:ss" /> : ""}</TableCell>
                                      <TableCell>{espJob.lastTriggeredBy}</TableCell>
                                      <TableCell align="right">{espJob.lastTriggeredDate !== null && espJob.lastTriggeredDate !== "" ? <Moment date={espJob.lastTriggeredDate} format="MM/DD/YYYY hh:mm:ss" /> : ""}</TableCell>
                                      <TableCell>
                                        {
                                          job.title === "Enrollment" ?
                                            <JobRunTicker interval={1} unit={'hours'} mode={1} />
                                            : job.title === "PreOrder (Match/Batch)" ?
                                              <JobRunTicker interval={30} unit={'minutes'} mode={1} />
                                              : null
                                        }
                                      </TableCell>
                                    </TableRow>
                                  ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>
                    <Box p={4}></Box>
                  </Box>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Box p={0} className={pageStyles.jobImageContainer}>
                        <Box p={3} className={pageStyles.jobTitle}>
                          &nbsp;{displayTabTitle(jobPagePathName)}
                        </Box>
                        <Box p={1}></Box>
                      </Box>
                    </Grid>

                    {/* Log Actions */}

                    <Grid item xs={12}>
                      <Grid container spacing={3}>
                        {/* Manage Log Status */}

                        <Grid item xs={12} md={6}>
                          <Grid container>
                            <Grid item xs={12}>
                              <Box className={globalStyles.topic}>
                                <Grid container>
                                  <Grid item xs={11}>
                                    <Typography variant="body1" component="p">
                                      Job Status
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={1}>
                                    <Box display="flex" justifyContent="end" alignItems="flex-end">
                                      {selectedEspJob.canWrite ? (
                                        <IconButton
                                          onClick={() => {
                                            writeLog("Jobs Tab " + jobPagePathName, "Job status update confirmation model opened.", "", "");
                                            setJobStatusDialogOpen(true);
                                          }}
                                        >
                                          {selectedEspJob.runStatus ? (
                                            <Tooltip title="Put on hold">
                                              <StopIcon fontSize="medium" color="error" />
                                            </Tooltip>
                                          ) : (
                                            <Tooltip title="Activate the job">
                                              <RunIcon fontSize="medium" color="secondary" />
                                            </Tooltip>
                                          )}
                                        </IconButton>
                                      ) : null}
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Grid>

                            <Grid item xs={12}>
                              <Box p={1} className={pageStyles.actionContent}>
                                <Typography variant="body2" component="p">
                                  <b>{selectedEspJob.runStatusDescription}</b>
                                  {selectedEspJob.note !== "" ? (
                                    <Typography variant="body2" component="p" color="error">
                                      <b>{selectedEspJob.note}</b>
                                    </Typography>
                                  ) : null}
                                </Typography>

                                <Box pt={1}>&nbsp;</Box>

                                <Typography variant="body2" component="p" style={{ flex: 1 }}>
                                  {selectedEspJob.lastModifiedDate !== null ? (
                                    <>
                                      Last updated on{" "}
                                      <b>
                                        <Moment date={selectedEspJob.lastModifiedDate} format="MM/DD/YYYY h:mm" />
                                      </b>
                                      &nbsp;by&nbsp;<b>{selectedEspJob.lastModifiedBy}</b>
                                    </>
                                  ) : (
                                    ""
                                  )}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>

                        {/* Trigger Batch Job */}
                        {selectedEspJob.triggerFileExists ?
                          <Grid item xs={12} md={6}>
                            <Grid container>
                              <Grid item xs={12}>
                                <Box className={globalStyles.topic}>
                                  <Grid container>
                                    <Grid item xs={11}>
                                      <Typography variant="body1" component="p">
                                        Trigger Batch Job
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={1}>
                                      <Box display="flex" justifyContent="end" alignItems="flex-end">
                                        {selectedEspJob.canWrite && selectedEspJob.runStatus ? (
                                          <IconButton
                                            onClick={() => {
                                              writeLog("Jobs Tab " + jobPagePathName, "Trigger file confirmation model Opened.", espJobKey, "job");
                                              setTriggerJobDialogOpen(true);
                                            }}
                                          >
                                            <Tooltip title="Trigger the job to run.">
                                              <TriggerIcon fontSize="medium" color="secondary" />
                                            </Tooltip>
                                          </IconButton>
                                        ) : null}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>

                              <Grid item xs={12}>
                                <Box p={1} className={pageStyles.actionContent}>
                                  <Typography variant="body2" component="p">
                                    <b>{selectedEspJob.pendingCount}</b> file(s) in queue as of&nbsp;
                                    {selectedEspJob.lastProcessDate !== null ? (
                                      <b>
                                        <Moment date={selectedEspJob.lastProcessDate} format="MM/DD/YYYY hh:mm:ss" />
                                      </b>
                                    ) : (
                                      ""
                                    )}
                                  </Typography>

                                  <Box p={1}>&nbsp;</Box>

                                  <Typography variant="body2" component="p" style={{ flex: 1 }}>
                                    {selectedEspJob.lastTriggeredDate !== null ? (
                                      <>
                                        Last triggered on{" "}
                                        <b>
                                          <Moment date={selectedEspJob.lastTriggeredDate} format="MM/DD/YYYY h:mm" />
                                        </b>
                                        &nbsp;by&nbsp;<b>{selectedEspJob.lastTriggeredBy}</b>
                                      </>
                                    ) : (
                                      ""
                                    )}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Grid>
                          : null}

                      </Grid>
                    </Grid>

                    {/* Log Detail */}

                    <Grid item xs={12}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Box className={globalStyles.topic}>
                            <Grid container>
                              <Grid item xs={11}>
                                <Typography variant="body1" component="p">
                                  Batch Log Files
                                </Typography>
                              </Grid>
                              <Grid item xs={1}>
                                <Box display="flex" justifyContent="end" alignItems="flex-end">
                                  {selectedEspJob.canWrite ? (
                                    <IconButton
                                      onClick={() => {
                                        writeLog("Jobs Tab " + jobPagePathName, "Refresh esp job data.", "", "");
                                        refreshBatchLog();
                                        handleGridMoreMenuClose();
                                      }}
                                    >
                                      <Tooltip title="Refresh log.">
                                        <RefreshIcon fontSize="medium" color="secondary" />
                                      </Tooltip>
                                    </IconButton>
                                  ) : null}
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>

                          <Box p={1}></Box>

                          <Box className={pageStyles.dataListContainer}>
                            <CustomDataGrid uniqueGridCode={"LogResult"} columns={log1Columns} rows={logData} showToolBar={true} sortModel={[{ field: "jobId", sort: "desc" }]} handleDataGridClick={handleDataGridClick} />
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Box>
        </Screen>
      )}
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    cardRoot: {
      width: "100%",
      backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[900] : theme.palette.grey[300],
      color: theme.palette.type === "dark" ? "#ffffff" : "#000000",
      paddingBottom: "20px",
      borderLeftWidth: 5,
      borderLeftColor: theme.palette.type === "dark" ? theme.palette.grey[700] : theme.palette.grey[400],
      borderLeftStyle: "solid",
    },
    actionContainer: {
      marginBottom: theme.spacing(4),
    },
    cardContent: {
      height: "230px",
      overflow: "auto",
    },
    dataListContainer: {
      minHeight: "400px",
      height: "calc(100vh - 550px)",
      width: "100%",
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        height: "450px",
      },
    },
    jobStatusContainer: {
      paddingBottom: 50,
      backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : "#F8FCFD",
    },
    jobImageContainer: {
      height: "100%",
      backgroundImage: `url(${JobStatusImage})`,
      backgroundPosition: "top center",
      backgroundSize: "cover",
      minHeight: "100px",
      clipPath: "polygon(0 0, 100% 0, 100% 100%, 0% 80%)",
      [theme.breakpoints.up("sm")]: {
        minHeight: "120px",
      },
    },
    jobTitle: {
      color: theme.palette.type === "dark" ? "#ffffff" : theme.palette.grey[200],
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      cursor: "pointer",
      letterSpacing: 1,
      fontSize: 18,
      fontWeight: "bolder",
      textTransform: "uppercase",
      textAlign: "left",
      padding: 0,
      paddingTop: 10,
      [theme.breakpoints.up("sm")]: {
        fontSize: 28,
      },
    },
    jobSuccessStatus: {
      color: "green",
    },
    jobFailStatus: {
      color: theme.palette.error.main,
      animationName: "$blinker",
      animationDuration: "2s",
      animationTimingFunction: "linear",
      animationIterationCount: "infinite",
    },
    "@keyframes blinker": {
      from: { opacity: 1 },
      to: { opacity: 0 },
    },
    actionContent: {
      minHeight: "150px",
      paddingBottom: theme.spacing(3),
    },
  })
);

export default Jobs;
